import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthService, AzureADDirectService } from '@fitzaa/shared';
import { User, Organization } from '@fitzaa/shared';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, signInWithCredential, GoogleAuthProvider as FirebaseGoogleProvider, onAuthStateChanged, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { firebaseConfig, firebaseFunctionsConfig, getEnvironment, isDebugMode } from '../config/firebase';
import { mapAzureADUserWeb, sendCustomEmailVerification } from '../services/WebCloudFunctionsService';
import { secureConfigService } from '../services/SecureConfigService';
import { errorInterceptor } from '../services/ErrorInterceptor';

// Initialize Firebase only if not already initialized
let app: FirebaseApp;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
  if (isDebugMode()) {
    console.log('[AuthContext] Firebase app initialized');
  }
} else {
  app = getApps()[0];
  if (isDebugMode()) {
    console.log('[AuthContext] Firebase app already initialized');
  }
}

// Initialize Auth with session persistence (not local persistence)
const auth = getAuth(app);
setPersistence(auth, browserSessionPersistence);

// Initialize Database
const database = getDatabase(app);
if (isDebugMode()) {
  console.log('[AuthContext] Firebase Database initialized');
}

// Initialize Azure AD Direct Service with secure configuration
let azureADService: AzureADDirectService | null = null;

const initializeAzureADService = async () => {
  try {
    const detectedEnvironment = getEnvironment();
    if (isDebugMode()) {
      console.log('[AuthContext] 🔍 Detected environment:', detectedEnvironment);
    }

    // Clear any cached configurations to ensure we get fresh data
    secureConfigService.clearCache();
    if (isDebugMode()) {
      console.log('[AuthContext] 🗑️ Cleared cached configurations');
    }

    const azureConfig = await secureConfigService.getAzureADConfig(detectedEnvironment);
    azureADService = new AzureADDirectService({
      clientId: azureConfig.clientId,
      tenantId: azureConfig.tenantId,
      redirectUri: azureConfig.redirectUri,
      scopes: azureConfig.scopes
    });
    if (isDebugMode()) {
      console.log('[AuthContext] ✅ Azure AD service initialized with secure config for environment:', detectedEnvironment);
    }
  } catch (error) {
    console.error('[AuthContext] ❌ Failed to initialize Azure AD service:', error);
  }
};

// Create AuthService instance with Firebase Functions configuration
const authService = new AuthService(app, getEnvironment());
if (isDebugMode()) {
  console.log('[AuthContext] AuthService created with Firebase app');
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authProvider: 'firebase' | 'azure' | 'google' | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithAzureAD: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithAzureAD: () => Promise<void>;
  logout: () => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [authProvider, setAuthProvider] = useState<'firebase' | 'azure' | 'google' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const authListenerSetupRef = useRef(false);
  const azureCallbackProcessingRef = useRef(false);

  // Memoize isAuthenticated to prevent unnecessary re-renders
  const isAuthenticated = useMemo(() => !!user, [user]);

  // Debug log only when isAuthenticated changes
  useEffect(() => {
    if (isDebugMode()) {
      console.log('[AuthContext] isAuthenticated changed to:', isAuthenticated, 'user:', user);
    } else {
      console.log('[AuthContext] isAuthenticated changed to:', isAuthenticated, 'user: [user details hidden - set REACT_APP_DEBUG=true to show]');
    }
  }, [isAuthenticated, user]);

  // Initialize Azure AD service on component mount
  useEffect(() => {
    if (isDebugMode()) {
      console.log('[AuthContext] 🔍 Component mounted, initializing Azure AD service...');
      console.log('[AuthContext] 🔍 Current environment:', getEnvironment());
      console.log('[AuthContext] 🔍 User authenticated:', !!user);
    }

    if (user) {
      initializeAzureADService();
    } else {
      if (isDebugMode()) {
        console.log('[AuthContext] ⏳ Waiting for user authentication before initializing Azure AD service...');
      }
    }
  }, [user]);

  // Function to create user profile in Firebase (now handled by AuthService)
  const createUserProfile = async (firebaseUser: any) => {
    try {
      if (isDebugMode()) {
        console.log('[AuthContext] Creating user profile for:', firebaseUser.uid);
      } else {
        console.log('[AuthContext] Creating user profile for: [user ID hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Create a default user profile
      // Don't assign organizationId for new users - they need to choose during onboarding
      const userProfile = {
        displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        email: firebaseUser.email || '',
        profileImageURL: firebaseUser.photoURL || undefined,
        organizationId: undefined,
        organizationName: undefined,
        role: 'member',
        joinedDate: new Date().toISOString(),
        streak: 0,
        totalPoints: 0,
      };

      // Save user profile to database using AuthService
      // Use a type assertion to bypass TypeScript error
      await (authService as any).saveUserProfileToDatabase(firebaseUser.uid, userProfile);
      if (isDebugMode()) {
        console.log('[AuthContext] ✅ User profile created successfully');
      }
    } catch (error) {
      console.error('[AuthContext] Error creating user profile:', error);
      // Don't throw error as this is not critical for sign-in
    }
  };

  // Helper function to get role emoji
  const getRoleEmoji = (role?: string) => {
    switch (role) {
      case 'super_admin':
        return '👑';
      case 'org_admin':
        return '⚡';
      case 'team_lead':
        return '🎯';
      case 'member':
      default:
        return '👤';
    }
  };

  // Helper function to get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Detect authentication provider from stored data
  const detectAuthProvider = (): 'firebase' | 'azure' | 'google' | null => {
    if (typeof window === 'undefined') return null;

    // Check for Azure AD tokens
    const azureTokens = sessionStorage.getItem('azuread.tokens');
    if (azureTokens) {
      try {
        const tokenData = JSON.parse(azureTokens);
        const now = Date.now();
        const tokenAge = now - tokenData.timestamp;
        const tokenExpiry = tokenData.expires_in * 1000;

        if (tokenAge < tokenExpiry) {
          return 'azure';
        }
      } catch (error) {
        console.error('[AuthContext] Error parsing Azure tokens:', error);
      }
    }

    // Check for Google auth in localStorage
    const googleAuth = localStorage.getItem('firebase:authUser:AIzaSyC3D0zx-G04S6OZVFfG5Mraj2aul_EOXAE:[DEFAULT]');
    if (googleAuth) {
      try {
        const googleData = JSON.parse(googleAuth);
        if (googleData.providerData && googleData.providerData.some((p: any) => p.providerId === 'google.com')) {
          return 'google';
        }
      } catch (error) {
        console.error('[AuthContext] Error parsing Google auth:', error);
      }
    }

    // Check for Firebase auth in localStorage
    const firebaseAuth = localStorage.getItem('firebase:authUser:AIzaSyC3D0zx-G04S6OZVFfG5Mraj2aul_EOXAE:[DEFAULT]');
    if (firebaseAuth) {
      try {
        const firebaseData = JSON.parse(firebaseAuth);
        if (firebaseData.providerData && firebaseData.providerData.some((p: any) => p.providerId === 'password')) {
          return 'firebase';
        }
      } catch (error) {
        console.error('[AuthContext] Error parsing Firebase auth:', error);
      }
    }

    return null;
  };

  // Process Azure AD callback
  const processAzureADCallback = async () => {
    if (azureCallbackProcessingRef.current) {
      if (isDebugMode()) {
        console.log('[AuthContext] Azure AD callback already being processed, skipping...');
      }
      return;
    }

    // Check if logout was recently completed
    const logoutCompleted = sessionStorage.getItem('auth.logout.completed');
    if (logoutCompleted === 'true') {
      if (isDebugMode()) {
        console.log('[AuthContext] Logout recently completed, skipping Azure AD callback processing');
      }
      sessionStorage.removeItem('auth.logout.completed');
      navigate('/login');
      return;
    }

    azureCallbackProcessingRef.current = true;
    // Set flag in sessionStorage to coordinate with AuthService
    sessionStorage.setItem('auth.azure.callback.inprogress', 'true');
    sessionStorage.setItem('auth.provider.switch.inprogress', 'true');
    if (isDebugMode()) {
      console.log('[AuthContext] Processing Azure AD callback...');
    }

    try {
      // Initialize Azure AD service if not already done
      if (!azureADService) {
        await initializeAzureADService();
      }

      if (!azureADService) {
        throw new Error('Failed to initialize Azure AD service');
      }

      // Check if there are tokens in the URL (either in hash fragment or search params)
      const hasTokensInHash = window.location.hash.includes('access_token=');
      const hasTokensInSearch = window.location.search.includes('access_token=');
      const hasCodeInSearch = window.location.search.includes('code=');

      if (hasTokensInHash || hasTokensInSearch || hasCodeInSearch) {
        if (isDebugMode()) {
          console.log('[AuthContext] Found tokens or code in URL, processing...');
        }

        // Process the authentication using completeAuth
        const { user, tokens } = await azureADService.completeAuth(window.location.href);
        if (isDebugMode()) {
          console.log('[AuthContext] ✅ Azure AD authentication successful');
        }

        // Store tokens in sessionStorage
        sessionStorage.setItem('azuread.tokens', JSON.stringify({
          ...tokens,
          timestamp: Date.now()
        }));

        const userEmail = user.email;
        const userDisplayName = user.displayName;

        // Only log sensitive info in debug mode
        if (isDebugMode()) {
          console.log('[AuthContext] Azure AD user info:', { userEmail, userDisplayName });
        } else {
          console.log('[AuthContext] Azure AD user info:', {
            userEmail: userEmail ? 'SET' : 'NOT SET',
            userDisplayName: userDisplayName ? 'SET' : 'NOT SET'
          });
        }

        // Create organization object
        const organization: Organization = {
          id: 'default-org',
          name: 'University Hospitals',
          description: 'Default organization',
          domain: 'uhhospitals.org',
          logo: '',
          createdAt: new Date().toISOString(),
          settings: {
            allowPublicJoin: true,
            customChallenges: true,
            leaderboardVisible: true,
            requireApproval: false,
            teamCreationOpen: true
          }
        };

        if (isDebugMode()) {
          console.log('[AuthContext] Calling mapAzureADUserWeb with:', userEmail, userDisplayName, organization.id);
        } else {
          console.log('[AuthContext] Calling mapAzureADUserWeb with: [details hidden - set REACT_APP_DEBUG=true to show]');
        }
        const mappedUser = await mapAzureADUserWeb(userEmail, userDisplayName, organization.id);
        if (isDebugMode()) {
          console.log('[AuthContext] ✅ Mapped user data received:', mappedUser);
        } else {
          console.log('[AuthContext] ✅ Mapped user data received: [user data hidden - set REACT_APP_DEBUG=true to show]');
        }

        // Generate the same deterministic password that was used in mapAzureADUser
        const generateDeterministicPassword = (email: string): string => {
          const secret = "AzureAD_Fitzaa_2024";
          const combined = email + secret;
          let hash = 0;
          for (let i = 0; i < combined.length; i++) {
            const char = combined.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
          }
          const basePassword = Math.abs(hash).toString(36).slice(0, 8);
          return `AzureAD_${basePassword}_2024!`;
        };

        const password = generateDeterministicPassword(userEmail);
        // Only log sensitive info in debug mode
        if (isDebugMode()) {
          console.log('[AuthContext] 🔐 Signing in with Firebase Auth using email/password');
        } else {
          console.log('[AuthContext] 🔐 Signing in with Firebase Auth using email/password (password hidden)');
        }

        // Sign in with Firebase Auth
        const { signInWithEmailAndPassword } = await import('firebase/auth');
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();

        try {
          await signInWithEmailAndPassword(auth, userEmail, password);
          if (isDebugMode()) {
            console.log('[AuthContext] ✅ Firebase Auth sign-in successful');
          }
        } catch (signInError: any) {
          // If it's an invalid credential error, try to update the password via cloud function
          if (signInError.code === 'auth/invalid-credential') {
            try {
              if (isDebugMode()) {
                console.log('[AuthContext] 🔄 Invalid credential, attempting to update password...');
              }
              const { updateUserPassword } = await import('../services/WebCloudFunctionsService');
              await updateUserPassword(userEmail);
              if (isDebugMode()) {
                console.log('[AuthContext] ✅ Password updated, trying sign-in again...');
              }
              // Try sign-in again with the updated password
              await signInWithEmailAndPassword(auth, userEmail, password);
              if (isDebugMode()) {
                console.log('[AuthContext] ✅ Firebase Auth sign-in successful after password update');
              }
            } catch (updateError: any) {
              console.error('[AuthContext] ❌ Failed to update password:', updateError);
              throw signInError; // Throw the original error
            }
          } else {
            throw signInError;
          }
        }

        // Now update the AuthService state
        await authService.signInWithAzureAD(mappedUser as any, organization);
        if (isDebugMode()) {
          console.log('[AuthContext] signInWithAzureAD completed');
        }

        // Update auth provider
        setAuthProvider('azure');

        // Clear the URL and redirect to home
        window.history.replaceState({}, document.title, '/');
        navigate('/dashboard');
      } else {
        if (isDebugMode()) {
          console.log('[AuthContext] No tokens or code found in URL');
        }
        navigate('/login');
      }
    } catch (error: any) {
      console.error('[AuthContext] ❌ Azure AD authentication failed:', error);
      // Clear the URL and redirect to login on error
      window.history.replaceState({}, document.title, '/');
      navigate('/login');
    } finally {
      azureCallbackProcessingRef.current = false;
      // Clear the sessionStorage flags
      sessionStorage.removeItem('auth.azure.callback.inprogress');
      sessionStorage.removeItem('auth.provider.switch.inprogress');
    }
  };

  // Single auth state listener - simplified and more efficient
  useEffect(() => {
    // Prevent multiple initializations
    if (authListenerSetupRef.current) {
      if (isDebugMode()) {
        console.log('[AuthContext] Auth listener already set up, skipping...');
      }
      return;
    }

    if (isDebugMode()) {
      console.log('[AuthContext] Setting up unified auth state listener...');
    }
    authListenerSetupRef.current = true;

    // Detect initial auth provider
    const initialProvider = detectAuthProvider();
    setAuthProvider(initialProvider);
    if (isDebugMode()) {
      console.log('[AuthContext] Initial auth provider detected:', initialProvider);
    }

    // Listen to AuthService state changes
    const unsubscribeAuthService = authService.subscribe((state) => {
      if (isDebugMode()) {
        console.log('[AuthContext] AuthService state changed:', {
          isAuthenticated: state.isAuthenticated,
          isLoading: state.isLoading,
          userId: state.user?.id,
          userEmail: state.user?.email,
          user: state.user
        });
      } else {
        console.log('[AuthContext] AuthService state changed: [user details hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Check if logout was recently completed
      const logoutCompleted = sessionStorage.getItem('auth.logout.completed');
      if (logoutCompleted === 'true') {
        if (isDebugMode()) {
          console.log('[AuthContext] Logout recently completed, preventing state update');
        }
        sessionStorage.removeItem('auth.logout.completed');
        setUser(null);
        setIsLoading(false);
        return;
      }

      if (isDebugMode()) {
        console.log('[AuthContext] Setting user state to:', state.user);
      } else {
        console.log('[AuthContext] Setting user state to: [user details hidden - set REACT_APP_DEBUG=true to show]');
      }
      setUser(state.user);
      setIsLoading(state.isLoading);
    });

    return () => {
      if (isDebugMode()) {
        console.log('[AuthContext] Cleaning up auth listeners...');
      }
      authListenerSetupRef.current = false;
      unsubscribeAuthService();
    };
  }, []); // Keep empty dependency array to run only once

  // Handle Azure AD redirect response on app load
  useEffect(() => {
    // Only process the callback if on /callback and not already processing
    if (location.pathname === '/callback' && !azureCallbackProcessingRef.current) {
      // Check if logout was recently completed
      const logoutCompleted = sessionStorage.getItem('auth.logout.completed');
      if (logoutCompleted === 'true') {
        if (isDebugMode()) {
          console.log('[AuthContext] Logout recently completed, skipping Azure AD callback');
        }
        sessionStorage.removeItem('auth.logout.completed');
        navigate('/login');
        return;
      }

      processAzureADCallback();
    }
  }, [location.pathname]); // Only depend on pathname, not navigate

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await authService.signInWithEmail(email, password);
      setAuthProvider('firebase');
    } catch (error) {
      setIsLoading(false);
      // Handle authentication errors
      errorInterceptor.handleError(error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      // Set flag to indicate Google sign-in is in progress
      sessionStorage.setItem('auth.google.signin.inprogress', 'true');

      const result = await signInWithPopup(auth, provider);

      // Create user profile in Firebase
      await createUserProfile(result.user);

      // Clear the Google sign-in flag
      sessionStorage.removeItem('auth.google.signin.inprogress');

      // Set auth provider - the Firebase auth state listener will handle the rest
      setAuthProvider('google');
    } catch (error) {
      // Clear the Google sign-in flag on error
      sessionStorage.removeItem('auth.google.signin.inprogress');
      setIsLoading(false);

      // Debug logging
      console.log('[AuthContext] Google sign-in error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        name: (error as any)?.name,
        isNetworkError: (error as any)?.code === 'auth/network-request-failed'
      });

      // Handle authentication errors
      errorInterceptor.handleError(error);
      throw error;
    }
  };

  const loginWithAzureAD = async () => {
    setIsLoading(true);
    try {
      // Initialize Azure AD service if not already done
      if (!azureADService) {
        if (isDebugMode()) {
          console.log('[AuthContext] 🔄 Initializing Azure AD service for login...');
        }
        await initializeAzureADService();
      }

      if (!azureADService) {
        throw new Error('Failed to initialize Azure AD service');
      }

      // Build the Azure AD authorization URL
      const authURL = await azureADService.buildAuthURL();
      if (isDebugMode()) {
        console.log('[AzureAD] Redirecting to:', authURL);
      }

      // Get redirect URI from secure config for logging
      const detectedEnvironment = getEnvironment();
      const azureConfig = await secureConfigService.getAzureADConfig(detectedEnvironment);
      if (isDebugMode()) {
        console.log('[AzureAD] Expected redirect URI:', azureConfig.redirectUri);
      }

      // Redirect to Azure AD for authentication
      window.location.href = authURL;
      // No code after this will run until redirect completes
    } catch (error) {
      setIsLoading(false);
      // Handle authentication errors
      errorInterceptor.handleError(error);
      throw error;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setIsLoggingOut(true);
    try {
      if (isDebugMode()) {
        console.log('[AuthContext] Starting logout process...');
      }

      // Set logout completion flag FIRST to prevent auto-restoration
      sessionStorage.setItem('auth.logout.completed', 'true');
      if (isDebugMode()) {
        console.log('[AuthContext] Set logout completion flag');
      }

      // Clear Azure AD tokens
      sessionStorage.removeItem('azuread.tokens');
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared Azure AD tokens');
      }

      // Clear Azure AD callback processing flag
      sessionStorage.removeItem('auth.azure.callback.inprogress');
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared Azure AD callback processing flag');
      }

      // Clear provider switch flag
      sessionStorage.removeItem('auth.provider.switch.inprogress');
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared provider switch flag');
      }

      // Clear Google sign-in flag
      sessionStorage.removeItem('auth.google.signin.inprogress');
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared Google sign-in flag');
      }

      // Clear persisted user role
      sessionStorage.removeItem('auth.user.role');
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared persisted user role');
      }

      // Clear Firebase Auth localStorage to prevent auto-restoration
      if (typeof localStorage !== 'undefined') {
        const firebaseKeys = Object.keys(localStorage).filter(key =>
          key.startsWith('firebase:authUser:') ||
          key.startsWith('firebase:') ||
          key.includes('firebase')
        );
        firebaseKeys.forEach(key => {
          localStorage.removeItem(key);
          if (isDebugMode()) {
            console.log('[AuthContext] Cleared Firebase localStorage key:', key);
          }
        });
      }

      // Clear Firebase Auth persistence first
      await auth.signOut();
      if (isDebugMode()) {
        console.log('[AuthContext] Firebase auth signOut completed');
      }

      // Force clear all sessionStorage to prevent any auto-restoration
      sessionStorage.clear();
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared all sessionStorage');
      }

      // Set logout completion flag again after clearing sessionStorage
      sessionStorage.setItem('auth.logout.completed', 'true');
      if (isDebugMode()) {
        console.log('[AuthContext] Re-set logout completion flag');
      }

      // Logout from AuthService
      await authService.signOut();
      if (isDebugMode()) {
        console.log('[AuthContext] AuthService signOut completed');
      }

      // Clear user state
      setUser(null);
      setAuthProvider(null);
      if (isDebugMode()) {
        console.log('[AuthContext] Cleared user state');
        console.log('[AuthContext] Logout completed successfully');
      }
      navigate('/login');
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      setIsLoading(false);
      throw error;
    } finally {
      setIsLoggingOut(false);
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    try {
      // Create Firebase user account (skip Firebase's email verification, we'll use custom)
      await authService.signUpWithEmail(email, password, name, { skipEmailVerification: true });
      
      // Get the created user to send custom verification email
      const firebaseAuth = getAuth(app);
      const currentUser = firebaseAuth.currentUser;
      
      if (currentUser) {
        // Send custom email verification via Brevo
        await sendCustomEmailVerification(email, currentUser.uid, name);
      }
      
      setAuthProvider('firebase');
      setIsLoading(false);
      
      // Redirect to verification pending page
      navigate('/verify-email-pending', { 
        state: { email, displayName: name },
        replace: true 
      });
    } catch (error: any) {
      setIsLoading(false);
      
      // Log detailed error information for debugging
      if (isDebugMode()) {
        console.error('[AuthContext] Sign-up error details:', {
          code: error?.code,
          message: error?.message,
          customData: error?.customData,
          stack: error?.stack,
        });
      }
      
      errorInterceptor.handleError(error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    authProvider,
    login,
    loginWithGoogle,
    loginWithAzureAD,
    signInWithGoogle: loginWithGoogle,
    signInWithAzureAD: loginWithAzureAD,
    logout,
    signUp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 